import { useTheme } from '@mui/material'

/**
 * SVG drag handle component rendered at phase boundaries
 * Allows users to drag to adjust the timing of phases
 *
 * Handle types:
 * - 'start': Start of first phase (shifts all phases)
 * - 'end': End of last phase (extends/shrinks last phase)
 * - number: Intersection between phases (adjusts adjacent phases)
 */
export const PhaseIntersectionHandle = ({
  x,                    // X position of handle (pixels)
  y,                    // Y position (top of bar)
  height,               // Height of phase bars
  intersectionIndex,    // 'start' | 'end' | number (index between phases)
  onDragStart,          // (intersectionIndex, clientX) => void
  isDragging,           // Boolean - currently being dragged
  isHovered,            // Boolean - mouse over handle
  onHover,              // (intersectionIndex | null) => void
  diamondOffset = 28,   // Vertical distance from bar center to each diamond marker
}) => {
  const theme = useTheme()

  const handleMouseDown = (e) => {
    e.stopPropagation()
    e.preventDefault()
    onDragStart(intersectionIndex, e.clientX)
  }

  // Colors based on state
  const markerColor = isDragging
    ? theme.palette.primary.main
    : isHovered
      ? theme.palette.primary.light
      : theme.palette.grey[500]

  const lineColor = isDragging
    ? theme.palette.primary.main
    : isHovered
      ? theme.palette.grey[600]
      : 'transparent'

  const markerSize = isDragging ? 8 : isHovered ? 7 : 6

  return (
    <g
      data-drag-handle="true"
      onMouseEnter={() => onHover(intersectionIndex)}
      onMouseLeave={() => onHover(null)}
      onMouseDown={handleMouseDown}
      style={{ cursor: 'ew-resize' }}
    >
      {/* Invisible hit area (wider for easier clicking) */}
      <rect
        x={x - 10}
        y={y}
        width={20}
        height={height}
        fill="transparent"
      />

      {/* Vertical line (visible on hover/drag) */}
      <line
        x1={x}
        y1={y - 2 + diamondOffset}
        x2={x}
        y2={y + height}
        stroke={lineColor}
        strokeWidth={isDragging ? 4 : 3}
        strokeDasharray={isDragging ? 'none' : '3,2'}
        style={{ transition: 'stroke 0.15s ease' }}
      />

      {/* top diamond marker (always visible when interactive) */}
      <polygon
        points={`
          ${x},${y + height / 2 - markerSize - diamondOffset}
          ${x + markerSize},${y + height / 2 - diamondOffset}
          ${x},${y + height / 2 + markerSize - diamondOffset}
          ${x - markerSize},${y + height / 2 - diamondOffset}
        `}
        fill={markerColor}
        stroke={theme.palette.background.paper}
        strokeWidth={1.5}
        style={{
          transition: 'fill 0.15s ease',
          filter: isDragging ? 'drop-shadow(0 0 3px rgba(0,0,0,0.3))' : 'none'
        }}
      />

      {/* top diamond marker (always visible when interactive) */}
      <polygon
        points={`
          ${x},${y + height / 2 - markerSize + diamondOffset}
          ${x + markerSize},${y + height / 2 + diamondOffset}
          ${x},${y + height / 2 + markerSize + diamondOffset}
          ${x - markerSize},${y + height / 2 + diamondOffset}
        `}
        fill={markerColor}
        stroke={theme.palette.background.paper}
        strokeWidth={1.5}
        style={{
          transition: 'fill 0.15s ease',
          filter: isDragging ? 'drop-shadow(0 0 3px rgba(0,0,0,0.3))' : 'none'
        }}
      />

      {/* Grip dots (visible on hover/drag) */}
      {(isHovered || isDragging) && (
        <>
          <circle
            cx={x}
            cy={y + height / 2 - 12 - diamondOffset}
            r={2}
            fill={markerColor}
          />
          <circle
            cx={x}
            cy={y + height / 2 + 12 - diamondOffset}
            r={2}
            fill={markerColor}
          />
        </>
      )}
    </g>
  )
}
